import { hasOwn } from '@buyhatke/custom-lib-js';

import {
	balanceCoins,
	depositCoins,
	getCoinProperty,
	withdrawCoins,
	getCoinProps,
	inrCoins,
	getBasePropsFromMarket,
	getCountryProps,
} from './params';
import { store } from './store';

export const getRate = (coin, market = 0) => {
	const { currentRate } = store.getState();
	const receivedCoin = String(coin);
	const defaultRates = {
		lastTradePrice: 0,
		yes_price: 0,
		sellPrice: 0,
		buyPrice: 0,
		volume: {
			max: 0,
			min: 0,
			rate: 0,
			volume: 0,
		},
		inr_price: 0,
		deltaDay: 0,
	};

	const { pair } = getBasePropsFromMarket(market);
	let upperCoin = '';

	switch (true) {
		case !market:
			upperCoin = receivedCoin.toUpperCase();
			break;
		default:
			upperCoin = `${receivedCoin}${pair}`.toUpperCase();
	}

	if (
		inrCoins.includes(coin.toLowerCase()) &&
		hasOwn(currentRate, upperCoin)
	) {
		return currentRate[upperCoin];
	}

	const coinData = getCoinProps(receivedCoin);

	// check in coinParams which is list of all coins, spot + quick swap
	if (coinData) {
		return {
			lastTradePrice:
				market === 1 ? coinData.sellEstimate : coinData.sellEstimateINR,
			yes_price: 0,
			sellPrice:
				market === 1 ? coinData.sellEstimate : coinData.sellEstimateINR,
			buyPrice:
				market === 1 ? coinData.buyEstimate : coinData.buyEstimateINR,
			volume: {
				max: 0,
				min: 0,
				rate: coinData.buyEstimateINR,
				volume: 0,
			},
			inr_price: coinData.buyEstimateINR,
			deltaDay: 0,
		};
	}

	return defaultRates;
};

export const askBalance = coin => {
	const { cryptoBal } = store.getState();
	const lowerCoin = String(coin).toLowerCase();
	const defaultBalance = {
		val: 0,
		orders: 0,
		convRate: 0,
		convRateRAMEOS: 0,
		lockedVal: 0,
		lockedOrders: 0,
		lockedTill: null,
		lock1: 0,
		lock2: 0,
		lockUtility: 0,
	};

	if (balanceCoins.indexOf(lowerCoin) > -1 && hasOwn(cryptoBal, lowerCoin)) {
		return cryptoBal[lowerCoin];
	}

	return defaultBalance;
};

export const askMarketBalance = market => {
	const { walletBal } = store.getState();

	switch (market) {
		case 0:
			return walletBal;

		case 2: {
			const { val, orders, convRate } = askBalance('try');

			return {
				avble: val,
				schd: orders,
				convRate,
			};
		}

		default: {
			const { val, orders, convRate } = askBalance('usdt');

			return {
				avble: val,
				schd: orders,
				convRate,
			};
		}
	}
};

export const getConvRate = (() => {
	const memoCache = {
		from: '',
		to: '',
		convRates: {},
		rate: '',
	};

	return (from, to) => {
		const { convRates } = store.getState();

		if (
			from === memoCache.from &&
			memoCache.to === to &&
			memoCache.convRates === convRates
		) {
			return memoCache.rate;
		}

		memoCache.to = to;
		memoCache.from = from;
		memoCache.convRates = convRates;

		if (to === from) {
			memoCache.rate = 1;
			return 1;
		}

		const fromRate = convRates[from];
		if (to === 1) {
			memoCache.rate = 1 / fromRate;
			return memoCache.rate;
		}

		const toRate = convRates[to];
		if (from === 1) {
			memoCache.rate = toRate;
			return toRate;
		}

		memoCache.rate = toRate / fromRate;

		return memoCache.rate;
	};
})();

export const isDisabledDepo = (coin, type) => {
	const lowerCoin = coin.toLowerCase();
	const { disabledThingies } = store.getState();

	if (
		hasOwn(disabledThingies, 'depo') &&
		Array.isArray(disabledThingies.depo)
	) {
		return (
			!depositCoins.includes(lowerCoin) ||
			getCoinProperty(lowerCoin, 'disableDeposit') ||
			disabledThingies.depo.some(depoCoin => {
				const [coinpart, chain] = depoCoin.toLowerCase().split('-');

				if (chain) {
					return coinpart === lowerCoin && chain === type;
				}

				return coinpart === lowerCoin;
			})
		);
	}
	return false;
};

export const isDisabledWith = (coin, type) => {
	const lowerCoin = coin.toLowerCase();
	const { disabledThingies } = store.getState();

	if (
		hasOwn(disabledThingies, 'with') &&
		Array.isArray(disabledThingies.with)
	) {
		return (
			!withdrawCoins.includes(lowerCoin) ||
			getCoinProperty(lowerCoin, 'disableWithdraw') ||
			disabledThingies.with.some(withCoin => {
				const [coinpart, chain] = withCoin.toLowerCase().split('-');

				if (chain) {
					return coinpart === lowerCoin && chain === type;
				}

				return coinpart === lowerCoin;
			})
		);
	}
	return false;
};

export const isDisabledMint = (coin, type) => {
	const lowerCoin = coin.toLowerCase();
	const { disabledThingies } = store.getState();

	if (
		hasOwn(disabledThingies, 'mint') &&
		Array.isArray(disabledThingies.mint)
	) {
		return (
			getCoinProperty(lowerCoin, 'disableMint') ||
			disabledThingies.mint.some(mintCoin => {
				const [coinpart, chain] = mintCoin.toLowerCase().split('-');

				if (chain) {
					return coinpart === lowerCoin && chain === type;
				}

				return coinpart === lowerCoin;
			})
		);
	}
	return false;
};

export const isFDCoin = coin => {
	const { onGoingFDPlansCoinList } = store.getState();
	const { coinId } = getCoinProps(coin);

	return onGoingFDPlansCoinList.includes(coinId);
};

export const getFDReturnForCoin = coin => {
	const { onGoingFDPlansCoinListWithReturns } = store.getState();
	const { coinId } = getCoinProps(coin);

	return onGoingFDPlansCoinListWithReturns[coinId];
};

export const checkLinks = link => {
	const { selectedCountry } = store.getState();
	const countryProps = getCountryProps(selectedCountry);
	const check = !countryProps?.[`no${link}`];
	return check;
};
